import { useMemo } from 'react';

interface UseDotsArguments {
  activeIndex: number;
  totalSlides: number;
  maxDotsToDisplay?: number;
}

export interface DotRangeItemInterface {
  /**
   * Stable React key. Identifies the physical DOM node, which is recycled
   * across navigations so its CSS size transitions are never interrupted.
   */
  key: number;
  /**
   * The slide this dot currently represents.
   */
  slideIndex: number;
  /**
   * The dot's visual position in the row, applied as a CSS `order`.
   */
  order: number;
  isActive: boolean;
  isContinuation: boolean;
  isHidden: boolean;
}

/**
 * A hook that takes information about the active slide in a photo carousel
 * and returns an array of dot props (to be rendered by `PhotoCarousel.Dot`) that
 * visually represents the user's position inside of that set.
 *
 * @example
 * const dotsToRender = useDotsRange({
 *  activeIndex,
 *  totalSlides,
 *  maxDotsToDisplay,
 * });
 */
export const useDotsRange = (args: UseDotsArguments): Array<DotRangeItemInterface> => {
  const { activeIndex, totalSlides, maxDotsToDisplay = 5 } = args;

  /*
   * Memoize on the destructured primitives rather than on the `args`
   * object — callers invoke this with an inline object literal, so
   * depending on `[args]` would invalidate the memo on every render and
   * produce a fresh `Array<{...}>` reference.
   */
  return useMemo(() => {
    /* No need to show 1 or 0 dots. */
    if (totalSlides < 2 || maxDotsToDisplay < 2) return [];

    /**
     * Determine number of dots to display. Normally, it's `maxDotsToDisplay`
     * but if the actual number of slides is less, use that number instead.
     */
    const targetRange = Math.min(maxDotsToDisplay, totalSlides);

    /**
     * Calculate how many dots should be on each side of the active dot.
     * For even numbers, we'll have one more on one side.
     */
    const dotsOnLeft = Math.floor((targetRange - 1) / 2);
    const dotsOnRight = targetRange - 1 - dotsOnLeft;

    /**
     * Calculate the initial range, ensuring we don't exceed targetRange.
     */
    let min = Math.max(0, activeIndex - dotsOnLeft);
    let max = Math.min(activeIndex + dotsOnRight, totalSlides - 1);

    /**
     * Adjust the range if we're at the boundaries to ensure we show exactly
     * the number of dots equal to `targetRange`.
     */
    if (min === 0) {
      // We're at the left boundary, so extend to the right
      max = Math.min(targetRange - 1, totalSlides - 1);
    } else if (max === totalSlides - 1) {
      // We're at the right boundary, so extend to the left
      min = Math.max(0, totalSlides - targetRange);
    }

    /**
     * Render only the dots the user can actually see, plus one collapsed dot on
     * each side that the window slides in from. A page can hold many carousels,
     * so a dot per slide is markup we can't afford.
     *
     * The dots are a ring buffer: `key` is the physical DOM node and `order` is
     * where it sits in the row. As the window advances, every node keeps its
     * identity — and therefore its in-flight CSS size transition — while its
     * `order` shifts by one. The single node whose `order` wraps from one end of
     * the row to the other is always a collapsed (0-width, 0-margin) one, so the
     * jump is invisible and the row still animates as a conveyor.
     *
     * An `isContinuation` dot will be rendered by `PhotoCarousel.Dot` as a smaller dot
     * to give visual affordance that there are more slides beyond it.
     */
    const count = Math.min(totalSlides, targetRange + 2);
    const firstRendered = count === totalSlides ? 0 : min - 1;

    const items = Array.from({ length: count }, (_, order) => {
      const slideIndex = firstRendered + order;
      const isActive = slideIndex === activeIndex;

      return {
        key: ((slideIndex % count) + count) % count,
        slideIndex,
        order,
        isActive,
        isContinuation:
          !isActive &&
          ((slideIndex === min && min > 0) || (slideIndex === max && max < totalSlides - 1)),
        isHidden: slideIndex < min || slideIndex > max,
      };
    });

    // Sort by `key` so React sees a constant key sequence and never reorders the
    // DOM nodes. Visual position comes from `order` alone.
    return items.sort((a, b) => a.key - b.key);
  }, [activeIndex, totalSlides, maxDotsToDisplay]);
};
